// Timer puro (Etapa 12 — spec S03 "Sessão de crochê"). Regra invíolável #5
// do projeto: tempo é registrado APENAS como linhas em `time_entries`
// (src/db/schema.ts), NUNCA contado em memória. Este módulo é a função
// central: recebe um snapshot vindo do banco (started_at da sessão aberta —
// ou null — e as sessões já fechadas do projeto) mais um "now", e devolve a
// duração total sempre RECALCULADA a partir desses três valores. A UI
// (SessionScreen) pode ter um `setInterval` de 1s para atualizar a tela,
// mas o valor exibido sempre passa por aqui — nunca por um contador
// incrementado em JS que sobrevive independente do banco. Isso é o que
// garante o critério de aceite "matar o app com o cronômetro rodando e
// reabrir: tempo continuou correto" — não há nada para "perder" no kill,
// porque nada foi acumulado fora do banco para começar.
//
// Sem React, sem banco, sem I/O — só datas e aritmética. Testado
// exaustivamente em timer.test.ts (TDD: RED antes deste arquivo existir).

/** Limiar (spec S03 §Cronômetro) para considerar uma sessão aberta "implausível"
 * — ex.: app matou/dormiu com o cronômetro rodando e ninguém tocou em nada
 * por horas. Constante configurável, não hardcoded inline nas checagens. */
export const SESSION_IMPLAUSIBLE_THRESHOLD_MS = 4 * 60 * 60 * 1000; // 4h

export interface ClosedSession {
  startedAt: Date;
  endedAt: Date;
}

export interface ElapsedSecondsParams {
  /** `started_at` da linha aberta em `time_entries` (`ended_at IS NULL`), ou
   * `null` se não há sessão aberta para este projeto agora. */
  openSessionStartedAt: Date | null;
  /** Sessões já fechadas do mesmo projeto (`ended_at IS NOT NULL`). */
  closedSessions: ClosedSession[];
  now: Date;
}

/**
 * Duração total = Σ (sessões fechadas) + (now − started_at da sessão aberta,
 * se houver). Cada segmento é clampado a zero individualmente — protege
 * contra relógio do device andando para trás (now < started_at) e contra
 * dado corrompido (ended_at < started_at, não deveria acontecer via
 * `updateRecord`, mas defensivo): um segmento ruim nunca SUBTRAI do total,
 * na pior das hipóteses contribui zero.
 */
export function calculateElapsedSeconds(params: ElapsedSecondsParams): number {
  const closedSeconds = params.closedSessions.reduce((sum, session) => {
    const seconds = (session.endedAt.getTime() - session.startedAt.getTime()) / 1000;
    return sum + Math.max(0, seconds);
  }, 0);

  let openSeconds = 0;
  if (params.openSessionStartedAt) {
    const seconds = (params.now.getTime() - params.openSessionStartedAt.getTime()) / 1000;
    openSeconds = Math.max(0, seconds);
  }

  return closedSeconds + openSeconds;
}

export interface ImplausibleSessionParams {
  /** `started_at` da sessão aberta. */
  startedAt: Date;
  /** Timestamp do toque de contador (`counter_events.at`) mais recente
   * registrado para QUALQUER contador do projeto desde que esta sessão
   * abriu, ou `null` se nenhum toque aconteceu nesse intervalo. */
  lastActivityAt: Date | null;
  now: Date;
  thresholdMs?: number;
}

/**
 * Uma sessão aberta é "implausível" quando já passou mais que o limiar
 * desde a ÚLTIMA ATIVIDADE conhecida — o toque de contador mais recente
 * desde a abertura da sessão, ou o próprio `started_at` se nenhum toque
 * aconteceu.
 *
 * Decisão deliberada (documentada no relatório da task): NÃO usa apenas
 * `(now − started_at)`. Uma usuária que trabalha 5h seguidas tocando o
 * contador o tempo todo não deve ser interrompida por um diálogo de "você
 * continuou trabalhando?" só porque a sessão já dura mais que 4h — isso
 * seria um falso positivo irritante numa sessão real e longa. O sinal
 * verdadeiro de "isso provavelmente é um app morto/dormiu, não uma sessão
 * real" é a INATIVIDADE prolongada, não a duração em si. Por isso o
 * relógio de referência reinicia a cada toque de contador, não só no
 * início da sessão.
 *
 * Efeito prático: se a única atividade foi um toque isolado há muito tempo
 * (ex.: tocou 1x na primeira hora e depois o app morreu), a sessão AINDA é
 * marcada implausível quando o tempo desde ESSE toque ultrapassa o limiar
 * — não basta ter tocado uma vez no passado distante.
 */
export function isSessionImplausible(params: ImplausibleSessionParams): boolean {
  const threshold = params.thresholdMs ?? SESSION_IMPLAUSIBLE_THRESHOLD_MS;
  const reference =
    params.lastActivityAt && params.lastActivityAt.getTime() > params.startedAt.getTime()
      ? params.lastActivityAt
      : params.startedAt;
  return params.now.getTime() - reference.getTime() > threshold;
}

/** Formata segundos totais como HH:MM:SS (cada campo com 2 dígitos; horas
 * sem limite superior — uma sessão de 25h exibe "25:00:00", não "01:00:00"). */
export function formatDurationHms(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}
